"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Calendar, User, Tag, ChevronLeft } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 md:py-16 lg:py-24 animate-pulse">
        <div className="mb-8 h-10 w-32 rounded-md bg-muted" />
        <div className="mb-10 flex flex-col items-center">
          <div className="mb-4 h-6 w-24 rounded-lg bg-muted" />
          <div className="mb-6 h-12 w-3/4 rounded-lg bg-muted" />
          <div className="h-4 w-48 rounded bg-muted" />
        </div>
        <div className="mb-12 h-[400px] w-full rounded-md bg-muted" />
        <div className="mx-auto max-w-3xl space-y-4">
          <div className="h-4 w-full rounded bg-muted" />
          <div className="h-4 w-full rounded bg-muted" />
          <div className="h-4 w-5/6 rounded bg-muted" />
          <div className="h-4 w-4/6 rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-24 text-center">
        <h2 className="font-default text-2xl font-bold text-foreground mb-4">Error al cargar la noticia</h2>
        <p className="text-muted-foreground mb-8">{error}</p>
        <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md">
          <Link to="/HomePage">Volver al Inicio</Link>
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-24 text-center">
        <h2 className="font-default text-2xl font-bold text-foreground mb-4">Noticia no encontrada</h2>
        <p className="text-muted-foreground mb-8">El artículo que busca no está disponible en este momento.</p>
        <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md">
          <Link to="/HomePage">Volver al Inicio</Link>
        </Button>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl px-4 py-12 md:py-16 lg:py-24">
      {}
      <div className="mb-8">
        <Button variant="ghost" asChild className="-ml-4 text-muted-foreground hover:text-primary transition-colors">
          <Link to="/HomePage">
            <ChevronLeft className="mr-2 size-4" />
            Regresar a Noticias
          </Link>
        </Button>
      </div>

      {}
      <header className="mb-10 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex flex-wrap justify-center gap-2">
            {post.categories.map((cat, i) => (
              <span
                key={cat.id}
                data-index={i}
                className="rounded-md bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4 text-primary" />
              <span className="font-default">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-primary" />
              <time dateTime={post.date} className="font-default">
                {new Date(post.date).toLocaleDateString('es-MX', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-md shadow-md border border-border">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div
        className="mx-auto max-w-3xl font-default text-base leading-relaxed text-foreground/90 md:text-lg [&>blockquote]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>h2]:mb-4 [&>h2]:mt-10 [&>h2]:font-default [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-foreground [&>h3]:mb-3 [&>h3]:mt-8 [&>h3]:font-default [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-foreground [&>img]:my-8 [&>img]:h-auto [&>img]:w-full [&>img]:rounded-md [&>ol]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>p]:mb-6 [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mx-auto mt-12 max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="size-4 text-primary" />
            {post.tags.map((tag, i) => (
              <Button key={tag.id} data-index={i} variant="outline" size="sm" asChild className="h-7 text-xs rounded-md border-border hover:bg-primary hover:text-white transition-colors">
                <Link to={tag.link || "#"}>#{tag.name}</Link>
              </Button>
            ))}
          </div>
        </div>
      )}

      <Separator className="mx-auto mt-12 max-w-3xl mb-12 bg-border" />

      {}
      {post.author && (
        <div className="mx-auto max-w-3xl rounded-md bg-muted p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6 border border-border shadow-sm">
          <Avatar className="size-20 sm:size-24 border-2 border-white shadow-md shrink-0">
            <AvatarImage src={post.author.avatar?.['96'] || ""} alt={post.author.name} />
            <AvatarFallback className="text-2xl font-bold bg-primary text-white">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left flex-1">
            <h3 className="font-default text-xl font-bold text-foreground mb-2">
              Publicado por {post.author.name}
            </h3>
            <p className="text-muted-foreground mb-4 text-sm sm:text-base font-default">
              Colaborador especializado en la industria del acero y construcción. Siga nuestras publicaciones para mantenerse informado sobre las últimas tendencias y noticias de Kalisch Acero.
            </p>
            {post.author.link && (
              <Button variant="secondary" size="sm" asChild className="bg-primary text-white hover:bg-primary/90 rounded-md">
                <Link to={post.author.link}>Ver más artículos de {post.author.name}</Link>
              </Button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", per_page: 1 };
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="223486" id="single-post-template-section" className="w-full bg-background min-h-screen text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}